import { useEffect, useState } from 'react';
import { api } from '../api';
import ExportButton from '../components/ExportButton';

const ORDER_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: '待接单', color: 'bg-yellow-100 text-yellow-700' },
  accepted: { label: '业务员已接', color: 'bg-blue-100 text-blue-700' },
  approved: { label: '后勤审核通过', color: 'bg-indigo-100 text-indigo-700' },
  invoiced: { label: '已开票', color: 'bg-purple-100 text-purple-700' },
  shipped: { label: '已发货', color: 'bg-teal-100 text-teal-700' },
  completed: { label: '已完成', color: 'bg-green-100 text-green-700' },
  cancelled: { label: '已取消', color: 'bg-red-100 text-red-700' },
};

type SubTab = 'customers' | 'orders' | 'inventory' | 'applications' | 'codes' | 'portalOrders';

const EMPTY_CUST = { name: '', contact: '', phone: '', address: '', remark: '', password: '' };

export default function Customer() {
  const [tab, setTab] = useState<SubTab>('customers');
  const [customers, setCustomers] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [custInv, setCustInv] = useState<any[]>([]);
  const [prods, setProds] = useState<any[]>([]);
  const [apps, setApps] = useState<any[]>([]);

  // 激活码（客户自助开通的"钥匙"：批量生成 → 发给/卖给客户 → 客户自助开通）
  const [codes, setCodes] = useState<any[]>([]);
  const [codeStats, setCodeStats] = useState<any>({ unused: 0, used: 0, void: 0, total: 0 });

  // 门户自助购买订单（客户下单 → 微信支付自动发码 / 后台确认收款自动发码）
  const [portalOrders, setPortalOrders] = useState<any[]>([]);
  const [portalWxpayEnabled, setPortalWxpayEnabled] = useState(false);
  const [codeFilter, setCodeFilter] = useState<'all' | 'unused' | 'used' | 'void'>('all');
  const [showGen, setShowGen] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState<any>(null);
  const [genForm, setGenForm] = useState({ count: 10, plan: '标准版 · 年度授权', valid_days: 365, expire_days: 0, note: '' });

  // 客户表单
  const [showCust, setShowCust] = useState(false);
  const [editCust, setEditCust] = useState<any>(null);
  const [custForm, setCustForm] = useState({ ...EMPTY_CUST });
  const [autoEnable, setAutoEnable] = useState(true);

  // 专属账号凭据弹窗（一人一码：创建/重置后展示，供管理员转发客户）
  const [cred, setCred] = useState<any>(null);
  const [copied, setCopied] = useState(false);

  // 分配商品（多租户）
  const [showAssign, setShowAssign] = useState(false);
  const [assignCust, setAssignCust] = useState<any>(null);
  const [assignList, setAssignList] = useState<any[]>([]);
  const [assignSaving, setAssignSaving] = useState(false);

  // 订单表单
  const [showOrder, setShowOrder] = useState(false);
  const [orderForm, setOrderForm] = useState({ customer_id: '', items: [{ product_id: '', quantity: '', unit_price: '' }] });

  useEffect(() => {
    loadCustomers();
    loadApps();
    api('/products').then(setProds).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'orders') loadOrders();
    if (tab === 'inventory') loadCustInv();
    if (tab === 'applications') loadApps();
    if (tab === 'codes') loadCodes();
    if (tab === 'portalOrders') loadPortalOrders();
  }, [tab, codeFilter]);

  const loadCustomers = () => api('/customer/customers').then(setCustomers).catch(() => {});
  const loadOrders = () => api('/customer/orders').then(setOrders).catch(() => {});
  const loadCustInv = () => api('/customer/inventory').then(setCustInv).catch(() => {});
  const loadApps = () => api('/customer/applications').then(setApps).catch(() => {});
  const pendingApps = apps.filter((a) => a.status === 'pending').length;

  // 门户购买订单：列表 + 微信支付是否启用
  const loadPortalOrders = () => {
    api('/customer/portal-orders').then((d: any) => {
      setPortalOrders(d.rows || []);
      setPortalWxpayEnabled(!!d.wxpayEnabled);
    }).catch(() => {});
  };
  const confirmPortalOrder = async (o: any) => {
    if (!confirm(`确认收到「${o.name}」的款项并自动开通？\n系统将自动生成激活码、开通账号，并把登录信息短信发给客户（若已配置短信服务）。`)) return;
    try {
      const r: any = await api(`/customer/portal-orders/${o.order_no}/confirm`, { method: 'POST', body: JSON.stringify({}) });
      loadPortalOrders();
      if (r.issued && r.issued.password) {
        setCred({ title: r.issued.renewed ? '续费成功 · 账号已延长' : '已确认收款 · 账号已开通', code: r.issued.code, password: r.issued.password, enabled: true, phone: o.phone });
      } else {
        alert(r.issued?.renewed ? '已为该客户续费并延长有效期' : '已开通账号');
      }
    } catch (e: any) { alert(e.message || '操作失败'); }
  };

  // --- 激活码 ---
  const loadCodes = () => {
    api('/customer/activation-codes' + (codeFilter === 'all' ? '' : '?status=' + codeFilter))
      .then(setCodes).catch(() => {});
    api('/customer/activation-codes/stats').then(setCodeStats).catch(() => {});
  };

  const genCodes = async () => {
    const n = Number(genForm.count) || 1;
    if (n < 1 || n > 200) return alert('单次生成 1 ~ 200 个');
    setGenBusy(true);
    try {
      const r: any = await api('/customer/activation-codes/generate', {
        method: 'POST',
        body: JSON.stringify({
          count: n,
          plan: genForm.plan,
          valid_days: Number(genForm.valid_days) || null,
          expire_days: Number(genForm.expire_days) || null,
          note: genForm.note,
        }),
      });
      setGenResult(r);
      loadCodes();
    } catch (e: any) { alert(e.message || '生成失败'); }
    finally { setGenBusy(false); }
  };

  const voidCode = async (c: any) => {
    if (!confirm(`确认作废激活码 ${c.code}？`)) return;
    try { await api(`/customer/activation-codes/${c.id}/void`, { method: 'POST', body: JSON.stringify({}) }); loadCodes(); }
    catch (e: any) { alert(e.message || '操作失败'); }
  };
  const restoreCode = async (c: any) => {
    try { await api(`/customer/activation-codes/${c.id}/restore`, { method: 'POST', body: JSON.stringify({}) }); loadCodes(); }
    catch (e: any) { alert(e.message || '操作失败'); }
  };
  const delCode = async (c: any) => {
    if (!confirm(`确认删除激活码 ${c.code}？`)) return;
    try { await api(`/customer/activation-codes/${c.id}`, { method: 'DELETE' }); loadCodes(); }
    catch (e: any) { alert(e.message || '删除失败'); }
  };
  const copyText = (text: string, msg = '已复制') => {
    const done = () => alert(msg);
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
      else fallbackCopy(text, done);
    } catch { fallbackCopy(text, done); }
  };
  const copyOneCode = (c: any) => {
    copyText(
      `【荣牧科技 ERP 激活码】\n激活码：${c.code}\n开通地址：https://erp.rongmukj.com/portal/activate\n（小程序：登录页 →「我有激活码，立即开通」）\n一个激活码只能开通一个账号，请妥善保管。`,
      '激活码已复制，可直接微信发给客户'
    );
  };
  const copyAllGen = () => {
    if (!genResult?.codes?.length) return;
    copyText(genResult.codes.join('\n'), `已复制 ${genResult.codes.length} 个激活码`);
  };
  const CODE_STATUS: Record<string, { label: string; color: string }> = {
    unused: { label: '未使用', color: 'bg-green-100 text-green-700' },
    used: { label: '已使用', color: 'bg-slate-100 text-slate-500' },
    void: { label: '已作废', color: 'bg-red-100 text-red-600' },
  };

  // --- 客户 CRUD ---
  const saveCust = async () => {
    if (!custForm.name) return alert('请填写客户名称');
    try {
      if (editCust) {
        await api(`/customer/customers/${editCust.id}`, { method: 'PUT', body: JSON.stringify(custForm) });
        setShowCust(false); setEditCust(null); setCustForm({ ...EMPTY_CUST }); setAutoEnable(true); loadCustomers();
      } else {
        // 一人一码：不填编码/密码时由后端自动生成（客户编码 C002/C003…，专属密码随机）
        const r: any = await api('/customer/customers', {
          method: 'POST',
          body: JSON.stringify({ ...custForm, mp_enabled: autoEnable }),
        });
        setShowCust(false); setEditCust(null); setCustForm({ ...EMPTY_CUST }); setAutoEnable(true); loadCustomers();
        setCred({ title: '客户创建成功', code: r.code, password: r.password, enabled: r.mp_enabled });
      }
    } catch (e: any) { alert(e.message || '保存失败'); }
  };
  const delCust = async (id: number) => { if (!confirm('删除该客户？')) return; await api(`/customer/customers/${id}`, { method: 'DELETE' }); loadCustomers(); };
  const openEditCust = (c: any) => {
    setEditCust(c);
    setCustForm({ name: c.name, contact: c.contact || '', phone: c.phone || '', address: c.address || '', remark: c.remark || '', password: '' });
    setShowCust(true);
  };

  // 重置客户专属密码（一人一码；旧密码立即失效；有手机号且已配短信服务时自动下发）
  const resetPwd = async (c: any) => {
    const tip = c.phone
      ? `\n若已配置短信服务，新密码会自动短信发送到 ${c.phone}。`
      : '\n该客户未登记手机号，请手动转发新密码。';
    if (!confirm(`确认给「${c.name}」重新生成专属密码？\n旧密码将立即失效。${tip}`)) return;
    try {
      const r: any = await api(`/customer/customers/${c.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ send_sms: !!c.phone }),
      });
      loadCustomers();
      setCred({ title: '密码已重置', code: r.code, password: r.password, enabled: c.mp_enabled, phone: c.phone, sms: r.sms });
    } catch (e: any) { alert(e.message || '重置失败'); }
  };

  // 复制凭据（一句话转发客户）
  const copyCred = () => {
    if (!cred) return;
    const text =
      `【荣牧科技 ERP 登录信息】\n` +
      `客户编码：${cred.code}\n` +
      `专属密码：${cred.password}\n` +
      `电脑端：https://erp.rongmukj.com/portal\n` +
      `手机端：微信小程序「荣牧 ERP」\n` +
      `（密码仅此一次展示，请妥善保存并尽快修改）`;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
      } else fallbackCopy(text, done);
    } catch { fallbackCopy(text, done); }
  };
  const fallbackCopy = (text: string, done: () => void) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      done();
    } catch { alert('复制失败，请手动记录：' + text); }
  };

  // --- 客户自助开通申请 ---
  // 通过：自动建号（或复用同手机号客户）+ 生成专属密码 + 开通权限；后端若配了短信服务会自动下发
  const approveApp = async (a: any) => {
    if (!confirm(`确认通过「${a.name}」的开通申请？\n系统将自动生成客户编码与专属密码，并开通使用权限。`)) return;
    try {
      const r: any = await api(`/customer/applications/${a.id}/approve`, { method: 'POST', body: JSON.stringify({}) });
      loadApps(); loadCustomers();
      setCred({
        title: r.reused ? '已开通（该手机号已有客户）' : '客户已开通 · 专属账号已生成',
        code: r.code,
        password: r.password,
        enabled: true,
        phone: a.phone,
        sms: r.sms,
      });
    } catch (e: any) { alert(e.message || '操作失败'); }
  };

  const rejectApp = async (a: any) => {
    if (!confirm(`确认拒绝「${a.name}」的开通申请？`)) return;
    try {
      await api(`/customer/applications/${a.id}/reject`, { method: 'POST', body: JSON.stringify({}) });
      loadApps();
    } catch (e: any) { alert(e.message || '操作失败'); }
  };

  const APP_STATUS: Record<string, { label: string; color: string }> = {
    pending: { label: '待处理', color: 'bg-yellow-100 text-yellow-700' },
    approved: { label: '已开通', color: 'bg-green-100 text-green-700' },
    rejected: { label: '已拒绝', color: 'bg-gray-100 text-gray-500' },
  };

  // 释放 / 收回 客户 ERP 使用权限（网页门户与小程序共用此闸门）
  const toggleMp = async (c: any) => {
    const next = !c.mp_enabled;
    if (!confirm(next
      ? `确认给「${c.name}」开通 ERP 使用权限？\n开通后该客户才能登录网页版 / 小程序（不释放则一律登不进）。`
      : `确认收回「${c.name}」的 ERP 使用权限？\n收回后该客户将无法登录网页版 / 小程序，已登录的也会立即退出。`)) return;
    try {
      await api(`/customer/customers/${c.id}/mp-access`, { method: 'POST', body: JSON.stringify({ enabled: next }) });
      setCustomers((list) => list.map((x) => (x.id === c.id ? { ...x, mp_enabled: next } : x)));
    } catch (e: any) { alert(e.message || '操作失败'); }
  };

  // --- 分配商品 ---
  const openAssign = async (c: any) => {
    setAssignCust(c);
    setShowAssign(true);
    setAssignSaving(false);
    try {
      const list = await api(`/customer/customers/${c.id}/products`);
      setAssignList(list.map((p: any) => ({ ...p, assigned: !!p.assigned, price: p.custom_price ?? '' })));
    } catch { setAssignList([]); }
  };
  const toggleAssign = (id: number) => setAssignList((l) => l.map((p) => (p.id === id ? { ...p, assigned: !p.assigned } : p)));
  const setAssignPrice = (id: number, v: string) => setAssignList((l) => l.map((p) => (p.id === id ? { ...p, price: v } : p)));
  const saveAssign = async () => {
    setAssignSaving(true);
    try {
      await api(`/customer/customers/${assignCust.id}/products`, {
        method: 'PUT',
        body: JSON.stringify({ items: assignList.map((p) => ({ product_id: p.id, assigned: p.assigned, price: p.price === '' ? null : p.price })) }),
      });
      setShowAssign(false);
      alert('已保存客户商品授权');
    } catch (e: any) { alert(e.message || '保存失败'); }
    finally { setAssignSaving(false); }
  };
  const assignCount = assignList.filter((p) => p.assigned).length;

  // --- 订单 ---
  const createOrder = async () => {
    if (!orderForm.customer_id) return alert('请选择客户');
    const validItems = orderForm.items.filter((it) => it.product_id && Number(it.quantity) > 0);
    if (validItems.length === 0) return alert('请至少添加一个明细');
    await api('/customer/orders', {
      method: 'POST',
      body: JSON.stringify({
        customer_id: Number(orderForm.customer_id),
        items: validItems.map((it) => ({
          product_id: Number(it.product_id),
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price) || undefined,
        })),
      }),
    });
    setShowOrder(false); setOrderForm({ customer_id: '', items: [{ product_id: '', quantity: '', unit_price: '' }] }); loadOrders();
  };

  const approveOrder = async (id: number) => {
    if (!confirm('确认后勤审核通过？')) return;
    try { await api(`/customer/orders/${id}/approve`, { method: 'POST' }); loadOrders(); } catch (e: any) { alert(e.message); }
  };

  const delOrder = async (id: number) => { if (!confirm('删除该报货单？')) return; await api(`/customer/orders/${id}`, { method: 'DELETE' }); loadOrders(); };

  const addOrderItem = () => setOrderForm({ ...orderForm, items: [...orderForm.items, { product_id: '', quantity: '', unit_price: '' }] });
  const removeOrderItem = (i: number) => setOrderForm({ ...orderForm, items: orderForm.items.filter((_, idx) => idx !== i) });

  const orderTotal = orderForm.items.reduce((sum, it) => {
    const prod = prods.find((p) => p.id === Number(it.product_id));
    return sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || prod?.price || 0);
  }, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">客户管理与报货</h2>
          <p className="text-sm text-slate-500 mt-1">客户档案 → 报货(业务员接单→后勤审核→开票发货) → 客户库存追踪</p>
        </div>
      </div>

      {/* Tab */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[
          { key: 'customers' as const, label: '👤 客户列表' },
          { key: 'applications' as const, label: pendingApps > 0 ? `📩 开通申请 (${pendingApps})` : '📩 开通申请' },
          { key: 'codes' as const, label: codeStats.unused > 0 ? `🎟️ 激活码 (${codeStats.unused})` : '🎟️ 激活码' },
          { key: 'orders' as const, label: '📦 客户报货单' },
          { key: 'portalOrders' as const, label: '🛒 购买订单' },
          { key: 'inventory' as const, label: '📊 客户库存' },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-1.5 rounded-md text-sm transition ${tab === t.key ? 'bg-white shadow font-medium' : 'hover:bg-slate-200'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ===== 客户列表 ===== */}
      {tab === 'customers' && (
        <>
          <div className="flex justify-end">
            <button onClick={() => { setEditCust(null); setCustForm({ ...EMPTY_CUST }); setAutoEnable(true); setShowCust(true); }} className="bg-brand-600 text-white rounded-lg px-4 py-2 hover:bg-brand-700 text-sm">
              + 新增客户（自动生成专属密码）
            </button>
          </div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">名称</th><th>客户编码</th><th>联系人</th><th>电话</th><th>地址</th><th>权限 / 专属密码</th><th className="w-56">操作</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-medium">{c.name}</td>
                    <td className="p-3 font-mono text-xs">{c.code || '—'}</td>
                    <td className="p-3">{c.contact || '—'}</td>
                    <td className="p-3">{c.phone || '—'}</td>
                    <td className="p-3 text-xs max-w-[200px] truncate">{c.address || '—'}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1 items-start">
                        {c.mp_enabled ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">已开通</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">未开通</span>
                        )}
                        <span className={`text-[10px] ${c.has_password ? 'text-slate-400' : 'text-amber-600'}`}>
                          {c.has_password ? '密码已设置' : '⚠ 未设密码'}
                        </span>
                        <span className={`text-[10px] ${c.license_expire_at ? 'text-amber-600' : 'text-slate-400'}`}>
                          {c.license_expire_at ? `授权至 ${String(c.license_expire_at).slice(0, 10)}` : '永久授权'}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-x-2 gap-y-1">
                        <button onClick={() => openEditCust(c)} className="text-brand-600 hover:underline text-xs">编辑</button>
                        <button onClick={() => openAssign(c)} className="text-indigo-600 hover:underline text-xs">分配商品</button>
                        <button onClick={() => resetPwd(c)} className="text-amber-600 hover:underline text-xs">重置密码</button>
                        <button onClick={() => toggleMp(c)} className={c.mp_enabled ? 'text-red-500 hover:underline text-xs' : 'text-green-600 hover:underline text-xs font-medium'}>
                          {c.mp_enabled ? '收回权限' : '开通权限'}
                        </button>
                        <button onClick={() => delCust(c.id)} className="text-red-500 hover:underline text-xs">删除</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">暂无客户</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== 开通申请（小程序自助提交） ===== */}
      {tab === 'applications' && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800">
            💡 客户在小程序「申请开通」页提交后会出现在这里，并同时通知业务员。
            点 <strong>「通过并开通」</strong> → 系统自动分配<strong>客户编码 + 专属密码 + 开通权限</strong>；
            若服务器已配置短信服务，会<strong>自动把账号密码短信发给客户</strong>（未配置则弹出密码由您手动转发）。
          </div>
          <div className="flex justify-end">
            <button onClick={loadApps} className="px-4 py-2 border rounded-lg hover:bg-slate-50 text-sm">刷新</button>
          </div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">公司/客户名称</th><th>联系人</th><th>手机号</th><th>备注</th><th>提交时间</th><th>状态</th><th className="w-44">操作</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id} className={`border-t hover:bg-slate-50 ${a.status === 'pending' ? 'bg-yellow-50' : ''}`}>
                    <td className="p-3 font-medium">{a.name}</td>
                    <td className="p-3">{a.contact || '—'}</td>
                    <td className="p-3 font-mono text-xs">{a.phone}</td>
                    <td className="p-3 text-xs text-slate-500 max-w-[220px] truncate">{a.remark || '—'}</td>
                    <td className="p-3 text-xs text-slate-500">{(a.created_at || '').slice(0, 16)}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${APP_STATUS[a.status]?.color || 'bg-gray-100'}`}>
                        {APP_STATUS[a.status]?.label || a.status}
                      </span>
                      {a.customer_code && <div className="text-[10px] text-slate-400 mt-1 font-mono">{a.customer_code}</div>}
                    </td>
                    <td className="p-3">
                      {a.status === 'pending' ? (
                        <div className="flex gap-2 items-center">
                          <button onClick={() => approveApp(a)} className="text-white bg-brand-600 hover:bg-brand-700 rounded px-3 py-1 text-xs">通过并开通</button>
                          <button onClick={() => rejectApp(a)} className="text-red-500 hover:underline text-xs">拒绝</button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">已处理</span>
                      )}
                    </td>
                  </tr>
                ))}
                {apps.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">暂无开通申请</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== 激活码（客户自助开通：批量生成 → 发给客户 → 客户自己开通，全自动） ===== */}
      {tab === 'codes' && (
        <>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800">
            💡 <strong>激活码 = 客户自助开通的钥匙</strong>。批量生成后把码发给（或卖给）客户，客户在
            <strong>小程序登录页「我有激活码」</strong> 或网页 <strong>/portal/activate</strong> 输入，
            系统就会<strong>自动建号、自动生成专属密码、自动开通权限</strong>，凭据当场显示在客户屏幕上——
            <strong>无需短信、无需人工转发</strong>。每个码只能用一次。
          </div>

          <div className="grid grid-cols-4 gap-3">
            {[
              { label: '可用激活码', value: codeStats.unused, color: 'text-green-600' },
              { label: '已使用', value: codeStats.used, color: 'text-slate-600' },
              { label: '已作废', value: codeStats.void, color: 'text-red-500' },
              { label: '总数', value: codeStats.total, color: 'text-slate-800' },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-xl shadow p-4">
                <div className="text-xs text-slate-500">{c.label}</div>
                <div className={`text-xl font-bold mt-1 ${c.color}`}>{c.value ?? 0}</div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center gap-2">
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
              {([
                { k: 'all' as const, l: '全部' },
                { k: 'unused' as const, l: '未使用' },
                { k: 'used' as const, l: '已使用' },
                { k: 'void' as const, l: '已作废' },
              ]).map((f) => (
                <button key={f.k} onClick={() => setCodeFilter(f.k)} className={`px-3 py-1 rounded-md text-xs transition ${codeFilter === f.k ? 'bg-white shadow font-medium' : 'hover:bg-slate-200'}`}>
                  {f.l}
                </button>
              ))}
            </div>
            <button onClick={() => { setGenResult(null); setShowGen(true); }} className="bg-brand-600 text-white rounded-lg px-4 py-2 hover:bg-brand-700 text-sm">
              + 批量生成激活码
            </button>
          </div>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">激活码</th><th>套餐</th><th>授权天数</th><th>批次</th><th>状态 / 开通客户</th><th>创建时间</th><th className="w-48">操作</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-mono font-semibold tracking-wide">{c.code}</td>
                    <td className="p-3 text-xs">{c.plan || '—'}</td>
                    <td className="p-3 text-xs">{c.valid_days ? `${c.valid_days} 天` : '永久'}</td>
                    <td className="p-3 text-xs text-slate-400 font-mono">{c.batch_no || '—'}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1 items-start">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${CODE_STATUS[c.status]?.color || 'bg-gray-100'}`}>
                          {CODE_STATUS[c.status]?.label || c.status}
                        </span>
                        {c.customer_code && (
                          <span className="text-[10px] text-slate-400 font-mono">
                            → {c.customer_code} {(c.used_at || '').slice(0, 10)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-xs text-slate-500">{(c.created_at || '').slice(0, 10)}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-x-2 gap-y-1">
                        {c.status === 'unused' && (
                          <>
                            <button onClick={() => copyOneCode(c)} className="text-brand-600 hover:underline text-xs">复制发客户</button>
                            <button onClick={() => voidCode(c)} className="text-amber-600 hover:underline text-xs">作废</button>
                            <button onClick={() => delCode(c)} className="text-red-500 hover:underline text-xs">删除</button>
                          </>
                        )}
                        {c.status === 'void' && (
                          <>
                            <button onClick={() => restoreCode(c)} className="text-green-600 hover:underline text-xs">恢复</button>
                            <button onClick={() => delCode(c)} className="text-red-500 hover:underline text-xs">删除</button>
                          </>
                        )}
                        {c.status === 'used' && <span className="text-xs text-slate-400">已开通客户</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                {codes.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">还没有激活码，点右上角「批量生成激活码」</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== 客户报货单 ===== */}
      {tab === 'orders' && (
        <>
          <div className="flex justify-end gap-2">
            <ExportButton
              filename="客户报货单"
              fetchData={() => api('/customer/orders')}
              columns={[
                { header: '订单号', key: 'order_no' },
                { header: '客户', key: 'customer_name' },
                { header: '金额', key: 'total_amount' },
                { header: '状态', key: 'status', format: (r) => ORDER_STATUS[r.status]?.label || r.status },
                { header: '创建时间', key: 'created_at' },
              ]}
            />
            <button onClick={() => { setOrderForm({ customer_id: '', items: [{ product_id: '', quantity: '', unit_price: '' }] }); setShowOrder(true); }} className="bg-brand-600 text-white rounded-lg px-4 py-2 hover:bg-brand-700 text-sm">
              + 新建报货单
            </button>
          </div>

          {/* 统计卡片 */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: '总单数', value: orders.length },
              { label: '待接单', value: orders.filter((o) => o.status === 'pending').length, color: 'text-yellow-600' },
              { label: '待审核', value: orders.filter((o) => o.status === 'accepted').length, color: 'text-blue-600' },
              { label: '总金额', value: '¥' + orders.reduce((s, o) => s + (o.total_amount || 0), 0).toLocaleString(), color: 'text-green-600' },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-xl shadow p-4">
                <div className="text-xs text-slate-500">{c.label}</div>
                <div className={`text-xl font-bold mt-1 ${c.color || ''}`}>{c.value}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">订单号</th><th>客户</th><th>金额</th><th>状态</th><th>创建时间</th><th className="w-32">操作</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs">{o.order_no}</td>
                    <td className="p-3 font-medium">{o.customer_name || '—'}</td>
                    <td className="p-3 font-medium text-green-600">¥{(o.total_amount || 0).toLocaleString()}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${ORDER_STATUS[o.status]?.color || 'bg-gray-100'}`}>
                        {ORDER_STATUS[o.status]?.label || o.status}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-slate-500">{(o.created_at || '').split(' ')[0]}</td>
                    <td className="p-3 space-x-2">
                      {o.status === 'accepted' && (
                        <button onClick={() => approveOrder(o.id)} className="text-green-600 hover:underline text-xs font-medium">后勤审核</button>
                      )}
                      {['pending', 'accepted'].includes(o.status) && (
                        <button onClick={() => delOrder(o.id)} className="text-red-500 hover:underline text-xs">删除</button>
                      )}
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">暂无报货单</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== 客户库存 ===== */}
      {tab === 'inventory' && (
        <>
          <div className="flex justify-end mb-3">
            <ExportButton
              filename="客户库存"
              fetchData={() => api('/customer/inventory')}
              columns={[
                { header: '客户', key: 'customer_name' },
                { header: '联系人', key: 'contact' },
                { header: '电话', key: 'phone' },
                { header: '产品', key: 'product_name' },
                { header: '编码', key: 'code' },
                { header: '单位', key: 'unit' },
                { header: '累计采购量', key: 'total_purchased' },
                { header: '当前库存', key: 'current_stock' },
              ]}
            />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 mb-3">
            💡 客户库存视图：展示各客户的累计采购量和当前持有库存。当库存低于安全线时会显示预警。
          </div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">客户</th><th>联系人</th><th>电话</th><th>累计采购量</th><th>当前库存</th><th>状态</th>
                </tr>
              </thead>
              <tbody>
                {custInv.map((cv) => {
                  const isLow = cv.current_stock < 10;
                  return (
                    <tr key={cv.customer_id} className={`border-t hover:bg-slate-50 ${isLow ? 'bg-red-50' : ''}`}>
                      <td className="p-3 font-medium">{cv.customer_name}</td>
                      <td className="p-3 text-xs">{cv.contact || '—'}</td>
                      <td className="p-3 text-xs">{cv.phone || '—'}</td>
                      <td className="p-3">{cv.total_purchased || 0}</td>
                      <td className="p-3 font-bold">{cv.current_stock ?? 0}</td>
                      <td className="p-3">
                        {isLow ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 animate-pulse">⚠️ 库存不足</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">正常</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {custInv.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">暂无数据</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== 门户购买订单（客户自助下单 → 微信支付自动发码 / 后台确认收款自动发码） ===== */}
      {tab === 'portalOrders' && (
        <>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800">
            💡 客户在网页门户 / 小程序「购买 ERP 授权」页下单后会出现在这里。
            {portalWxpayEnabled
              ? '已接入微信支付，客户扫码付款后系统会自动生成激活码并开通账号，无需人工；此列表仅作对账。'
              : '当前未配置微信支付，客户下单后由您点「确认收款」→ 系统自动生成激活码、开通账号，并短信通知客户（若已配短信服务）。'}
          </div>
          <div className="flex justify-end">
            <button onClick={loadPortalOrders} className="px-4 py-2 border rounded-lg hover:bg-slate-50 text-sm">刷新</button>
          </div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">订单号</th><th>客户</th><th>手机号</th><th>套餐</th><th>金额</th><th>渠道</th><th>状态</th><th>下单时间</th><th className="w-44">操作</th>
                </tr>
              </thead>
              <tbody>
                {portalOrders.map((o) => (
                  <tr key={o.id} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs">{o.order_no}</td>
                    <td className="p-3 font-medium">{o.name || '—'}</td>
                    <td className="p-3 font-mono text-xs">{o.phone || '—'}</td>
                    <td className="p-3 text-xs">{o.plan_name || '—'}</td>
                    <td className="p-3 font-medium text-green-600">¥{((o.amount || 0) / 100).toLocaleString()}</td>
                    <td className="p-3 text-xs">{o.channel === 'free' ? '免费' : o.channel === 'manual' ? '后台确认' : '微信支付'}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${o.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {o.status === 'paid' ? '已开通' : '待收款'}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-slate-500">{(o.created_at || '').slice(0, 16)}</td>
                    <td className="p-3">
                      {o.status !== 'paid' ? (
                        <button onClick={() => confirmPortalOrder(o)} className="text-white bg-brand-600 hover:bg-brand-700 rounded px-3 py-1 text-xs">确认收款并开通</button>
                      ) : (
                        <span className="text-xs text-slate-400">{o.paid_at ? o.paid_at.slice(0, 16) : '已开通'}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {portalOrders.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-400">暂无购买订单</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* 客户弹窗 */}
      {showCust && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCust(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">{editCust ? '编辑客户' : '新增客户'}</h3>
            {!editCust && (
              <p className="text-xs text-slate-500 mb-3">
                一人一码：系统会自动分配<strong>唯一客户编码</strong>与<strong>专属密码</strong>，保存后请复制转发给客户。
              </p>
            )}
            <div className="space-y-3">
              <input className="w-full border rounded-lg px-3 py-2" placeholder="客户名称 *" value={custForm.name} onChange={(e) => setCustForm({ ...custForm, name: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2" placeholder="联系人" value={custForm.contact} onChange={(e) => setCustForm({ ...custForm, contact: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2" placeholder="电话" value={custForm.phone} onChange={(e) => setCustForm({ ...custForm, phone: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2" placeholder="地址" value={custForm.address} onChange={(e) => setCustForm({ ...custForm, address: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2" placeholder="备注" value={custForm.remark} onChange={(e) => setCustForm({ ...custForm, remark: e.target.value })} />
              {!editCust && (
                <input className="w-full border rounded-lg px-3 py-2" placeholder="专属密码（留空 → 系统自动生成，推荐）" value={custForm.password} onChange={(e) => setCustForm({ ...custForm, password: e.target.value })} />
              )}
              {!editCust && (
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={autoEnable} onChange={(e) => setAutoEnable(e.target.checked)} className="w-4 h-4" />
                  创建后立即开通使用权限（客户可马上登录网页版 / 小程序）
                </label>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowCust(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={saveCust} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* 报货单弹窗 */}
      {showOrder && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowOrder(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">新建客户报货单</h3>
            <select className="w-full border rounded-lg px-3 py-2 mb-4" value={orderForm.customer_id} onChange={(e) => setOrderForm({ ...orderForm, customer_id: e.target.value })}>
              <option value="">选择客户</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.contact || ''})</option>)}
            </select>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm">报货明细</span>
                <button onClick={addOrderItem} className="text-xs text-brand-600 hover:underline">+ 添加一行</button>
              </div>
              {orderForm.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select className="col-span-4 border rounded px-2 py-1.5 text-sm" value={it.product_id} onChange={(e) => { const cp = [...orderForm.items]; cp[i].product_id = e.target.value; setOrderForm({ ...orderForm, items: cp }); }}>
                    <option value="">选择产品</option>
                    {prods.map((p) => <option key={p.id} value={p.id}>{p.name} (¥{p.price})</option>)}
                  </select>
                  <input className="col-span-2 border rounded px-2 py-1.5 text-sm" type="number" placeholder="数量" value={it.quantity} onChange={(e) => { const cp = [...orderForm.items]; cp[i].quantity = e.target.value; setOrderForm({ ...orderForm, items: cp }); }} />
                  <input className="col-span-3 border rounded px-2 py-1.5 text-sm" type="number" placeholder="单价" value={it.unit_price} onChange={(e) => { const cp = [...orderForm.items]; cp[i].unit_price = e.target.value; setOrderForm({ ...orderForm, items: cp }); }} />
                  <span className="col-span-2 text-sm">¥{((Number(it.quantity) || 0) * (Number(it.unit_price) || prods.find((p) => p.id === Number(it.product_id))?.price || 0)).toLocaleString()}</span>
                  <button onClick={() => removeOrderItem(i)} className="col-span-1 text-red-500">×</button>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mb-4 p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">合计：</span>
              <span className="text-xl font-bold text-green-600">¥{orderTotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowOrder(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={createOrder} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">提交</button>
            </div>
          </div>
        </div>
      )}

      {/* 分配商品弹窗（多租户：给客户释放可售商品 + 专属价） */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAssign(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">给「{assignCust?.name}」分配商品</h3>
              <span className="text-xs text-slate-500">已选 {assignCount} 项</span>
            </div>
            <div className="text-xs text-slate-500 mb-3">
              勾选后该客户小程序端仅能看到这些商品；留空专属价则沿用全局价。全部取消勾选 = 该客户可看全部商品。
            </div>
            <div className="overflow-auto flex-1 border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr><th className="p-2 w-12 text-center">分配</th><th className="p-2 text-left">商品</th><th className="p-2 text-left">编码</th><th className="p-2 text-left">全局价</th><th className="p-2 text-left w-32">客户专属价</th></tr>
                </thead>
                <tbody>
                  {assignList.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-slate-50">
                      <td className="p-2 text-center">
                        <input type="checkbox" checked={!!p.assigned} onChange={() => toggleAssign(p.id)} className="w-4 h-4" />
                      </td>
                      <td className="p-2 font-medium">{p.name}</td>
                      <td className="p-2 text-xs text-slate-500">{p.code}</td>
                      <td className="p-2 text-slate-500">¥{p.price}</td>
                      <td className="p-2">
                        <input
                          type="number"
                          placeholder="沿用全局"
                          disabled={!p.assigned}
                          value={p.price ?? ''}
                          onChange={(e) => setAssignPrice(p.id, e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm disabled:bg-slate-100"
                        />
                      </td>
                    </tr>
                  ))}
                  {assignList.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">暂无商品</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAssign(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={saveAssign} disabled={assignSaving} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
                {assignSaving ? '保存中…' : '保存授权'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 激活码批量生成弹窗 */}
      {showGen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowGen(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">🎟️ 批量生成激活码</h3>
            {!genResult ? (
              <>
                <p className="text-xs text-slate-500 mb-4">
                  生成的激活码可打印、可微信转发、可随货附送。客户使用后自动开通账号，一个码只能用一次。
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-slate-500">生成数量（1 ~ 200）</label>
                    <input type="number" className="w-full border rounded-lg px-3 py-2" value={genForm.count}
                      onChange={(e) => setGenForm({ ...genForm, count: Number(e.target.value) })} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">套餐名称（客户激活时会看到）</label>
                    <input className="w-full border rounded-lg px-3 py-2" placeholder="如：标准版 · 年度授权"
                      value={genForm.plan} onChange={(e) => setGenForm({ ...genForm, plan: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">开通后授权天数（0 = 永久）</label>
                      <input type="number" className="w-full border rounded-lg px-3 py-2" value={genForm.valid_days}
                        onChange={(e) => setGenForm({ ...genForm, valid_days: Number(e.target.value) })} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">激活码本身有效期天数（0 = 不限）</label>
                      <input type="number" className="w-full border rounded-lg px-3 py-2" value={genForm.expire_days}
                        onChange={(e) => setGenForm({ ...genForm, expire_days: Number(e.target.value) })} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">备注（卖给谁 / 订单号 / 渠道，便于对账）</label>
                    <input className="w-full border rounded-lg px-3 py-2" placeholder="选填"
                      value={genForm.note} onChange={(e) => setGenForm({ ...genForm, note: e.target.value })} />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => setShowGen(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
                  <button onClick={genCodes} disabled={genBusy} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
                    {genBusy ? '生成中…' : '立即生成'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-emerald-700 mb-3">
                  ✅ 已生成 <strong>{genResult.count}</strong> 个激活码（批次 {genResult.batch_no}）。
                  请复制后发给客户；<strong>关闭后仍可在列表中查看和复制</strong>。
                </p>
                <textarea readOnly value={(genResult.codes || []).join('\n')} rows={8}
                  className="w-full border rounded-lg px-3 py-2 font-mono text-sm bg-slate-50" />
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => setShowGen(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">关闭</button>
                  <button onClick={copyAllGen} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">复制全部激活码</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 专属账号凭据弹窗（一人一码：创建 / 重置密码后展示，供管理员转发客户） */}
      {cred && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCred(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">🔑 {cred.title}</h3>
            <p className="text-xs text-amber-600 mb-4">密码仅此一次明文展示（数据库只存加密值），请立即复制保存并转发客户。</p>
            <div className="bg-slate-50 rounded-lg p-4 space-y-3 font-mono">
              <div>
                <div className="text-xs text-slate-500 font-sans mb-1">客户编码（登录账号）</div>
                <div className="text-xl font-bold tracking-wide">{cred.code}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-sans mb-1">专属密码</div>
                <div className="text-xl font-bold tracking-widest text-brand-700">{cred.password}</div>
              </div>
              <div className="text-xs font-sans">
                使用权限：
                {cred.enabled ? <span className="text-green-600 font-medium">已开通（可登录）</span> : <span className="text-red-500 font-medium">未开通（需在列表点「开通权限」）</span>}
              </div>
              {cred.sms && (
                <div className={`text-xs font-sans ${cred.sms.sent ? 'text-green-600' : 'text-amber-600'}`}>
                  短信下发：
                  {cred.sms.sent ? `已发送到 ${cred.phone || '客户手机'}` : cred.sms.msg}
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setCred(null)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">关闭</button>
              <button onClick={copyCred} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">
                {copied ? '✓ 已复制' : '复制登录信息'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
